/**
 * SettingsPage (Настройки) — app preferences, currently the theme switcher (ТЗ §7).
 *
 * The user picks Melusine / Тёмная / Системная; the choice applies instantly (via
 * ``useTheme`` → ``data-theme`` on the root) and is persisted to ``localStorage``.
 */
import { useBackButton } from "@melusine/shared-telegram";
import {
  saveThemeRemote,
  THEME_OPTIONS,
  useTheme,
  type Theme,
} from "@features/theme";
import { routes, useNavigate } from "@shared/router";
import { PromoCodeForm } from "@features/promo";
import { useCallback } from "react";

import styles from "./SettingsPage.module.css";

const CheckIcon = (): JSX.Element => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m5 13 4 4L19 7" />
  </svg>
);

export function SettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  useBackButton(() => {
    navigate(routes.home());
  });

  // Apply locally + persist to the profile (cross-device). Local apply is instant; the
  // server write is best-effort and must not break the UI if it fails (ТЗ §7).
  const selectTheme = useCallback(
    (next: Theme) => {
      setTheme(next);
      void saveThemeRemote(next).catch(() => {
        /* offline / transient — localStorage already holds the choice */
      });
    },
    [setTheme],
  );

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <button
          type="button"
          className={styles.back}
          aria-label="Назад"
          onClick={() => navigate(routes.home())}
        >
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
        </button>
        <h1 className={styles.title}>Настройки</h1>
      </header>

      <section className={styles.group} aria-label="Тема приложения">
        <h2 className={styles.groupTitle}>Тема приложения</h2>
        <div
          className={styles.options}
          role="radiogroup"
          aria-label="Выбор темы"
        >
          {THEME_OPTIONS.map((option) => {
            const selected = option.value === theme;
            return (
              <button
                key={option.value}
                type="button"
                className={styles.option}
                role="radio"
                aria-checked={selected}
                data-selected={selected}
                onClick={() => selectTheme(option.value)}
              >
                <span className={styles.optionText}>
                  <span className={styles.optionLabel}>{option.label}</span>
                  <span className={styles.optionDescription}>
                    {option.description}
                  </span>
                </span>
                <span className={styles.optionMark} aria-hidden="true">
                  {selected ? <CheckIcon /> : null}
                </span>
              </button>
            );
          })}
        </div>
        <p className={styles.note}>
          Тема сохраняется в профиле и подтянется на других устройствах.
        </p>
      </section>

      <section className={styles.group} aria-label="Промокод">
        <h2 className={styles.groupTitle}>Промокод</h2>
        <PromoCodeForm />
      </section>
    </div>
  );
}
